import React, { Fragment, useCallback } from 'react';
import PropTypes, { object } from 'prop-types';
import { NavLink } from 'react-router-dom';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { Chips } from '@p81-common/ui';

import { useRegionalLink } from 'hooks/useRegionalLink';

import {
  Table,
  Button,
  Text,
  Icon,
  TableCell
} from 'components/common/Elements/Elements';
import AdditionalContent from './AdditionalContent/AdditionalContent';

import { renameMacOS, underlineToSpace } from 'utils/common';

import classes from './RolloutVersionsTable.module.scss';
import { access, hasAccess, resources } from 'utils/permissions';

const TableRow = ({
  company,
  tableConf,
  row,
  mouseEnter,
  onOpenAdditionalModal,
  idOpenVersion,
  handleOpenFilesVersion
}) => {
  const hasAgentRolloutReadAccess = hasAccess([
    resources.CS_AGENT_ROLLOUT,
    access.READ
  ]);
  const { t } = useTranslation(['activity']);
  const generateRegionalPath = useRegionalLink();

  const generateAgentRolloutPath = useCallback(() => {
    const basePath = `/cs/agent-rollout/${company.platform}_${company.version}`;
    const regionalPath = generateRegionalPath(basePath);

    // Parse the regional path to separate pathname and search
    const [pathname, search = ''] = regionalPath.split('?');

    return {
      pathname,
      search: search ? `?${search}` : '',
      state: {
        agent: company,
        buildIds: company.files.map(({ buildId }) => buildId)
      }
    };
  }, [company, generateRegionalPath]);

  return (
    <Table.Row className={classes.row}>
      {tableConf.map(({ show, field }, ind) => {
        const cellProps = {
          row: row,
          mouseEnter: mouseEnter,
          ind: ind,
          field: field,
          classes: classes
        };

        return (
          <Fragment key={`${field}-${company.buildId}}`}>
            {show && field === 'platform' && (
              <TableCell cellProps={cellProps}>
                <Button
                  className={cn(
                    classes.button,
                    classes.button_wrap,
                    idOpenVersion === company._id && classes.button_open
                  )}
                  onClick={() => {
                    handleOpenFilesVersion(company._id);
                  }}
                  aria-label="Open/close the Tunnels div"
                >
                  <Icon
                    size="16px"
                    icon="arrow"
                    className={
                      idOpenVersion !== company._id
                        ? classes.iconDown
                        : classes.iconUp
                    }
                  />
                  {idOpenVersion === company._id && (
                    <Icon
                      icon="parentLine"
                      size="73px"
                      className={cn(classes.lineDot)}
                    />
                  )}
                </Button>
                <Text type="s">{renameMacOS(company.platform)}</Text>
              </TableCell>
            )}
            {show && field === 'version' && (
              <TableCell cellProps={cellProps}>{company.version}</TableCell>
            )}
            {show && field === 'status' && (
              <TableCell cellProps={cellProps}>
                {underlineToSpace(company.status.join(', '))}
              </TableCell>
            )}

            {show && field === 'releaseTracks' && (
              <TableCell cellProps={cellProps}>
                {(company.releaseTracks || []).map(track => (
                  <Chips
                    key={track}
                    label={t(`activity:agentRollout.RELEASE_TRACK_${track}`)}
                    size="small"
                  />
                ))}
              </TableCell>
            )}

            {show && field === 'numOfFiles' && (
              <TableCell cellProps={cellProps}>
                {company.files.length}
              </TableCell>
            )}

            {show && field === 'appliedTo' && (
              <TableCell cellProps={cellProps}>
                {!isNaN(company.appliedTo) ? (
                  <Button
                    className={classes.appliedTo_button}
                    onClick={() =>
                      onOpenAdditionalModal({
                        content: (
                          <AdditionalContent data={company.files[0].buildId} />
                        )
                      })
                    }
                  >
                    {company.appliedTo}
                  </Button>
                ) : (
                  company.appliedTo
                )}
              </TableCell>
            )}
            {show && field === 'buttons' && (
              <TableCell cellProps={cellProps}>
                {company.platform !== 'linux' && hasAgentRolloutReadAccess && (
                  <NavLink
                    className={classes.linkSelect}
                    to={generateAgentRolloutPath()}
                  >
                    Select
                  </NavLink>
                )}
              </TableCell>
            )}
          </Fragment>
        );
      })}
    </Table.Row>
  );
};

TableRow.propTypes = {
  company: PropTypes.object,
  tableConf: PropTypes.arrayOf(object),
  row: PropTypes.string,
  mouseEnter: PropTypes.func,
  onOpenAdditionalModal: PropTypes.func.isRequired,
  handleOpenFilesVersion: PropTypes.func.isRequired,
  idOpenVersion: PropTypes.string
};

export default TableRow;
